import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileText } from 'lucide-react'
import { useState } from 'react'

import { Loader } from '@/components/common/Loader'
import { toast } from '@/components/common/Toast'
import { LiffShell } from '@/components/liff/LiffShell'
import { Button } from '@/components/ui'
import { BILL_LOCATION_LABELS, type BillLocationValue } from '@/constants/bill-location'
import {
  printLiffOutstandingBillPdf,
  useGetLiffOutstandingBill,
} from '@/modules/bill/services/liff-outstanding'
import { formatNumber } from '@/utils/number'

export const Route = createFileRoute('/liff/outstanding/$billId')({
  component: LiffOutstandingDetailPage,
})

function LiffOutstandingDetailPage() {
  const { billId } = Route.useParams()
  const search = Route.useSearch({ strict: false }) as { tab?: string }
  const navigate = useNavigate()
  const [printing, setPrinting] = useState(false)

  const { data: bill, isPending, isError } = useGetLiffOutstandingBill(billId)

  const remaining =
    bill?.remaining_amount ?? bill?.payment_information?.remaining_amount ?? 0
  const total = bill?.total_amount ?? 0
  const paid = bill?.paid_amount ?? bill?.payment_information?.total_paid ?? 0
  const loc = bill?.location as BillLocationValue | undefined
  const latestRound = bill?.payment_rounds?.[bill.payment_rounds.length - 1]

  const handlePdf = async () => {
    setPrinting(true)
    try {
      const roundForPdf =
        remaining > 0.01 && latestRound ? latestRound.round_no : undefined
      await printLiffOutstandingBillPdf(billId, roundForPdf)
    } catch (e) {
      toast.toastFailed(e instanceof Error ? e.message : 'โหลด PDF ไม่สำเร็จ')
    } finally {
      setPrinting(false)
    }
  }

  if (isPending) {
    return (
      <LiffShell title="รายละเอียดบิล" hideNav>
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      </LiffShell>
    )
  }

  if (isError || !bill) {
    return (
      <LiffShell title="รายละเอียดบิล" hideNav>
        <p className="text-center text-destructive">ไม่พบบิลหรือโหลดไม่สำเร็จ</p>
        <Button
          type="button"
          variant="outline"
          className="mx-auto mt-4 flex h-12"
          onClick={() =>
            navigate({
              to: '/liff/outstanding',
              search: { tab: search.tab === 'buy' ? 'buy' : 'sell' },
            })
          }
        >
          กลับรายการ
        </Button>
      </LiffShell>
    )
  }

  return (
    <LiffShell title="รายละเอียดบิล" hideNav>
      <div className="mx-auto max-w-md space-y-4">
        <Link
          to="/liff/outstanding"
          search={{ tab: search.tab === 'buy' ? 'buy' : 'sell' }}
          className="inline-flex min-h-[44px] items-center gap-2 text-base font-medium text-slate-700"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
          กลับรายการค้างชำระ
        </Link>

        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <p className="font-mono text-sm text-slate-500">{bill.bill_number ?? '—'}</p>
          <h2 className="mt-1 text-xl font-bold text-slate-900">
            {bill.customer_information?.name ?? '—'}
          </h2>
          {bill.customer_information?.phone ? (
            <p className="mt-1 text-base text-slate-600">{bill.customer_information.phone}</p>
          ) : null}

          <dl className="mt-4 space-y-2 text-sm">
            <Row label="ประเภท" value={bill.bill_information?.bill_type === 'sell' ? 'ขาย' : 'ซื้อ'} />
            <Row label="สาขา" value={loc ? (BILL_LOCATION_LABELS[loc] ?? loc) : '—'} />
            <Row label="วันที่บิล" value={bill.bill_date ?? '—'} />
          </dl>
        </div>

        <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-5">
          <p className="text-sm font-medium text-rose-800">ยอดค้างชำระ</p>
          <p className="text-3xl font-bold text-rose-600">{formatNumber(remaining)} บาท</p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500">ยอดบิล</p>
              <p className="font-semibold">{formatNumber(total)}</p>
            </div>
            <div>
              <p className="text-slate-500">ชำระแล้ว</p>
              <p className="font-semibold">{formatNumber(paid)}</p>
            </div>
          </div>
        </div>

        {bill.line_items && bill.line_items.length > 0 ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-base font-semibold text-slate-800">รายการสินค้า</h3>
            <ul className="space-y-2">
              {bill.line_items.map((li, i) => (
                <li
                  key={li.id ?? i}
                  className="flex justify-between gap-2 border-b border-border/40 py-2 text-sm last:border-0"
                >
                  <span className="text-slate-700">
                    {[li.durian_type, li.grade, li.size].filter(Boolean).join(' ') || 'รายการ'}
                    {li.weight_kg ? ` · ${li.weight_kg} กก.` : ''}
                  </span>
                  <span className="shrink-0 font-medium">
                    {li.line_total != null ? formatNumber(Number(li.line_total)) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {bill.payment_rounds && bill.payment_rounds.length > 0 ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-base font-semibold text-slate-800">ประวัติรับชำระ</h3>
            <ul className="space-y-2">
              {bill.payment_rounds.map((r) => (
                <li
                  key={r.id}
                  className="rounded-xl bg-slate-50 px-3 py-2 text-sm"
                >
                  <span className="font-medium">งวดที่ {r.round_no}</span>
                  <span className="float-right font-semibold text-blue-700">
                    {formatNumber(r.paid_amount)} ฿
                  </span>
                  <p className="mt-1 text-xs text-slate-500">
                    คงค้าง {formatNumber(r.remaining_after)} ฿
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <Button
          type="button"
          className="h-14 w-full text-lg font-semibold"
          disabled={printing}
          onClick={() => void handlePdf()}
        >
          <FileText className="mr-2 h-5 w-5" aria-hidden />
          {printing ? 'กำลังเปิด PDF...' : 'ดูใบเสร็จ PDF'}
        </Button>
      </div>
    </LiffShell>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  )
}
